'use client';

import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ExpandableCallout, Select, TextInput } from '@galapagos/design-system/web';
import { US_STATES } from '@galapagos/shared/registration';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivacyNote } from '../components/PrivacyNote';
import { PrivateFields } from '../components/PrivateFields';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import {
  ADDRESS_MAX_LENGTH,
  EMPTY_ADDRESS_FORM,
  hasAddressErrors,
  toProfileAddress,
  validateAddress,
  withZip,
  type AddressDraft,
  type AddressErrors,
  type AddressForm,
} from '../profile/profile-answers';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { EarlierAnswersGate } from './EarlierAnswersGate';

const STATE_OPTIONS = US_STATES.map((state) => ({ value: state.code, label: state.name }));

function AddressStep() {
  const { t } = useTranslation('signup');
  const { goToNextStep } = useSignupNavigation();
  const setProfileAddress = useSignupFlowStore((state) => state.setProfileAddress);
  const [form, setForm] = useState<AddressForm>(EMPTY_ADDRESS_FORM);
  const { draft } = form;
  const [errors, setErrors] = useState<AddressErrors>({});
  // As in production, a ZIP fills the state only until the member chooses one:
  // an empty state, or one an earlier ZIP filled, may change. Re-picking the
  // value the ZIP already filled fires no change, so the pick itself is
  // watched; opening the list or tabbing past the field is not a choice. Which state the ZIP
  // filled lives in the form state, so every update stays pure.
  const statePicked = useRef(false);
  const title = t('address.title');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();

  // Editing a field clears its own error; the rest wait for the next Continue.
  const update = (field: keyof AddressDraft) => (value: string) => {
    setForm((current) => ({ ...current, draft: { ...current.draft, [field]: value } }));
    setErrors((current) => (field in current ? { ...current, [field]: undefined } : current));
  };

  const onZipChange = (value: string) => {
    const stateChosen = statePicked.current;
    // Reads the latest form, so several fields autofilled in one go all land.
    setForm((current) => withZip(current, value, stateChosen));
    setErrors((current) => ({
      ...current,
      zip: undefined,
      ...(stateChosen ? {} : { state: undefined }),
    }));
  };

  // Only a real pick counts: a click on an option, or Enter or Space while
  // the list is open. Opening and dismissing the list does not. A pick that
  // changes the value also arrives through onChange.
  const onStatePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest('[role="option"]')) {
      statePicked.current = true;
    }
  };

  const onStateKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const listOpen = event.currentTarget.querySelector('[aria-expanded="true"]') !== null;
    if (listOpen && (event.key === 'Enter' || event.key === ' ')) statePicked.current = true;
  };

  const onStatePick = (value: string) => {
    statePicked.current = true;
    update('state')(value);
  };

  const onContinue = () => {
    const found = validateAddress(draft);
    setErrors(found);
    if (hasAddressErrors(found)) return;
    const address = toProfileAddress(draft);
    setProfileAddress(address);
    goToNextStep('address');
  };

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        <ExpandableCallout
          title={t('address.infoTitle')}
          actionId="signup.address-screen-why.toggle"
          data-testid="signup-address-why"
        >
          {t('address.infoBody')}
        </ExpandableCallout>
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status" />
        <PrivateFields>
          <TextInput
            label={t('address.line1Label')}
            value={draft.line1}
            errorText={
              errors.line1 ? t(errors.line1, { max: ADDRESS_MAX_LENGTH.line1 }) : undefined
            }
            autoComplete="address-line1"
            name="address-line1"
            onChangeText={update('line1')}
            data-testid="signup-address-line1"
          />
          <TextInput
            label={t('address.line2Label')}
            value={draft.line2}
            errorText={
              errors.line2 ? t(errors.line2, { max: ADDRESS_MAX_LENGTH.line2 }) : undefined
            }
            autoComplete="address-line2"
            name="address-line2"
            onChangeText={update('line2')}
            data-testid="signup-address-line2"
          />
          <TextInput
            label={t('address.cityLabel')}
            value={draft.city}
            errorText={errors.city ? t(errors.city, { max: ADDRESS_MAX_LENGTH.city }) : undefined}
            autoComplete="address-level2"
            name="address-city"
            onChangeText={update('city')}
            data-testid="signup-address-city"
          />
          <TextInput
            label={t('address.zipLabel')}
            value={draft.zip}
            errorText={errors.zip ? t(errors.zip) : undefined}
            autoComplete="postal-code"
            inputMode="numeric"
            name="address-zip"
            onChangeText={onZipChange}
            data-testid="signup-address-zip"
          />
          <div onPointerDownCapture={onStatePointerDown} onKeyDownCapture={onStateKeyDown}>
            <Select
              actionId="signup.address-screen-state.change"
              toggleActionId="signup.address-screen-state.toggle"
              label={t('address.stateLabel')}
              options={STATE_OPTIONS}
              value={draft.state}
              errorText={errors.state ? t(errors.state) : undefined}
              name="address-state"
              onChange={onStatePick}
              data-testid="signup-address-state"
            />
          </div>
        </PrivateFields>
        <PrivacyNote text={t('address.privacyNote')} />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            onPress={onContinue}
            actionId="signup.address-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </div>
    </SignupShell>
  );
}

/** `/address/`: where the member lives (v9 1.6). */
export function AddressStepScreen() {
  return (
    <EarlierAnswersGate step="address">
      <AddressStep />
    </EarlierAnswersGate>
  );
}
